import { history, historyField } from "@codemirror/commands";
import { EditorState as CMEditorState } from "@codemirror/state";
import { createContext, useContext, useEffect, useRef, useState } from "react";

import createLocalScriptStorage from "pages/DevelopWorkspace/contexts/ScriptsContext/localScriptsStorage";
import {
  EditorState,
  LocalScript,
  RemoteScript,
  ScriptType,
  StoredLocalScript,
  StoredRemoteScript,
} from "pages/DevelopWorkspace/workspace.types";

export type Script = LocalScript | RemoteScript;
export type StoredScript = StoredLocalScript | StoredRemoteScript;

export interface ScriptStorage {
  listScripts(): Promise<Script[]>;
  loadScript(id: string): Promise<StoredScript | null>;
  updateScript(script: Script): Promise<Script>;
  deleteScript(id: string): Promise<boolean>;
  createScript(
    id: string,
    title: string,
    editorState?: EditorState
  ): Promise<LocalScript>;
}

export enum ScriptStatus {
  loading = "loading",
  error = "error",
  success = "success",
  creating = "creating",
}

interface ScriptState {
  script: Script | null;
  status: ScriptStatus;
  isUnsaved: boolean;
}

export interface ScriptsContextState {
  localScripts: {
    [key: string]: ScriptState;
  };
  remoteScripts: {
    [key: string]: ScriptState;
  };
}

interface ScriptsContextValue {
  state: ScriptsContextState;
  actions: {
    loadScript: (id: string, type: ScriptType) => void;
    updateScript: (
      id: string,
      type: ScriptType,
      data: {
        title?: string;
        editorState?: EditorState;
      }
    ) => void;
    createScript: (
      newScriptId: string,
      type: ScriptType,
      data: { title: string; editorState: EditorState }
    ) => Promise<LocalScript>;
    removeScript: (id: string, type: ScriptType) => void;
  };
}

interface ScriptsContextProviderProps {
  children: React.ReactNode;
}

const ScriptsContext = createContext<ScriptsContextValue | undefined>(
  undefined
);

const localScriptStorageInstance = createLocalScriptStorage();

export const ScriptsContextProvider = (props: ScriptsContextProviderProps) => {
  const { children } = props;

  const localScriptStorage = useRef(localScriptStorageInstance);

  const [state, setState] = useState<ScriptsContextState>({
    localScripts: {},
    remoteScripts: {},
  });

  // check unsaved scripts each 0.5 second, and run updateScript to sync with localForage
  useEffect(() => {
    const interval = setInterval(() => {
      const unsavedScripts = Object.entries(state.localScripts).filter(
        ([, scriptState]) => {
          return scriptState.isUnsaved;
        }
      );

      unsavedScripts.forEach(async ([id, scriptState]) => {
        const script = scriptState.script;

        if (!script) {
          return;
        }

        try {
          const updatedScript =
            await localScriptStorage.current.updateScript(script);

          // check if script title/editorState was updated while we were saving
          // if yes, we don't update state (keep unsaved flag = true)

          const isTitleUpdated = script.title !== updatedScript.title;
          const isEditorStateUpdated =
            script.editorState?.doc !== updatedScript.editorState?.doc;

          if (isTitleUpdated || isEditorStateUpdated) {
            return;
          }

          setState(prevState => {
            return {
              ...prevState,
              localScripts: {
                ...prevState.localScripts,
                [id]: {
                  ...prevState.localScripts[id],
                  isUnsaved: false,
                },
              },
            };
          });
        } catch (e) {
          console.log("updateScript error", e);
        }
      });
    }, 500);

    return () => {
      clearInterval(interval);
    };
  }, [state]);

  const createScript = async (
    newScriptId: string,
    type: ScriptType,
    data: {
      title: string;
      editorState: EditorState;
    }
  ): Promise<LocalScript> => {
    if (type === ScriptType.remote) {
      throw new Error("Remote scripts are not supported yet");
    }

    setState(prevState => {
      return {
        ...prevState,
        localScripts: {
          ...prevState.localScripts,
          [newScriptId]: {
            script: null,
            status: ScriptStatus.creating,
            isUnsaved: false,
          },
        },
      };
    });

    const script = await localScriptStorage.current.createScript(
      newScriptId,
      data.title,
      { ...data.editorState, original: data.editorState.original ?? null }
    );

    setState(prevState => {
      return {
        ...prevState,
        localScripts: {
          ...prevState.localScripts,
          [newScriptId]: {
            script,
            status: ScriptStatus.success,
            isUnsaved: false,
          },
        },
      };
    });

    return script;
  };

  const loadScript = async (id: string, type: ScriptType) => {
    if (type === ScriptType.local) {
      setState(prevState => {
        return {
          ...prevState,
          localScripts: {
            ...prevState.localScripts,
            [id]: {
              script: null,
              status: ScriptStatus.loading,
              isUnsaved: false,
            },
          },
        };
      });

      try {
        const script = await localScriptStorage.current.loadScript(id);

        if (!script) {
          throw new Error("not found");
        }

        // Lazy migration for scripts created before editorState was introduced.
        // If a script has no editorState, create a default one from legacy content (or empty string)
        // and save it back to localForage to enforce the runtime invariant that editorState always exists.
        if (!script.editorState) {
          const sourceContent =
            "content" in script && typeof script.content === "string"
              ? script.content
              : "";

          const tempState = CMEditorState.create({
            doc: sourceContent,
            extensions: [history()],
          });

          const json = tempState.toJSON({ history: historyField }) as any;
          script.editorState = {
            ...json,
            original: null,
          } as unknown as EditorState;

          await localScriptStorage.current.updateScript(script);
        }

        // Normalize legacy editorState that lacks the `original` field to avoid
        if (script.editorState.original === undefined) {
          script.editorState.original = null;
          await localScriptStorage.current.updateScript(script);
        }

        setState(prevState => {
          return {
            ...prevState,
            localScripts: {
              ...prevState.localScripts,
              [id]: {
                script: {
                  ...script,
                  // Keep legacy content present in-memory so older versions remain compatible on rollback
                  content: script.editorState?.doc ?? "",
                } as StoredLocalScript,
                status: ScriptStatus.success,
                isUnsaved: false,
              },
            },
          };
        });
      } catch (e) {
        setState(prevState => {
          return {
            ...prevState,
            localScripts: {
              ...prevState.localScripts,
              [id]: {
                script: null,
                status: ScriptStatus.error,
                isUnsaved: false,
              },
            },
          };
        });
      }
    }
  };

  const updateScript = async (
    id: string,
    type: ScriptType,
    data: {
      title?: string;
      editorState?: EditorState;
    }
  ) => {
    if (type === ScriptType.local) {
      setState(prevState => {
        const prevScript = prevState.localScripts[id].script;

        if (!prevScript) {
          return prevState;
        }

        const newScriptData = {
          ...prevScript,
          title: data.title ?? prevScript.title,
          content: data.editorState?.doc ?? "",
          editorState: data.editorState ?? prevScript?.editorState,
        };

        return {
          ...prevState,
          localScripts: {
            ...prevState.localScripts,
            [id]: {
              ...prevState.localScripts[id],
              script: newScriptData,
              isUnsaved: true,
            },
          },
        };
      });
    }
  };

  const removeScript = async (id: string, type: ScriptType) => {
    if (type === ScriptType.local) {
      const scriptState = state.localScripts[id];

      if (!scriptState) {
        return;
      }

      const script = scriptState.script;

      if (!script) {
        return;
      }

      setState(prevState => {
        const newState = {
          ...prevState,
          localScripts: {
            ...prevState.localScripts,
          },
        };

        delete newState.localScripts[id];

        return newState;
      });

      await localScriptStorage.current.deleteScript(id);
    }
  };

  // eslint-disable-next-line react/jsx-no-constructed-context-values -- it's ok here
  const contextValue = {
    state,
    actions: {
      loadScript,
      updateScript,
      removeScript,
      createScript,
    },
  };

  return (
    <ScriptsContext.Provider value={contextValue}>
      {children}
    </ScriptsContext.Provider>
  );
};

export const useScripts = () => {
  const context = useContext(ScriptsContext);
  if (!context) {
    throw new Error(
      "useScriptsContext must be used within a ScriptsContextProvider"
    );
  }
  return context;
};
